import { Base } from './Base';
import { MealItem } from './MealItem';
import {Footprint, IFootprint} from './Footprint';
import {SingleCategoryStatistic} from './Statistics';
import {Recipe} from "./Recipe";

export class Food {
    id: string;
    name: string;
    caloriesPerKg: number;
    get caloriesPer100g(): number { return this.caloriesPerKg / 10; }
    footprintPerKg: number;
    get emissionsPer100g(): number { return this.footprintPerKg * 1000 / 10; }
    footprint: Footprint;
    get emissionsPerCalorie(): number { return this.footprintPerKg / this.caloriesPerKg * 1000; }
    carbsPer100g: number;
    carbsKnown = false;
    fatPer100g: number;
    fatKnown = false;
    proteinPer100g: number;
    proteinKnown = false;
    get macrosKnown(): boolean { return this.carbsKnown && this.proteinKnown && this.fatKnown; }
    wastePercentage: number;
    possibleUnits: Unit[];
    get baseUnit(): string { return this.possibleUnits && this.possibleUnits.length > 0 ? this.possibleUnits[0].baseUnit : 'g'; }
    category: string;
    subCategory: string;
    get imgUrl(): string { return this.category ? '/assets/icon/categories/' + this.category + '.svg' : '/assets/icon/categories/Vegetables.svg' ; }
    lastChangedAt: Date;

    get categoryStatisticEmissions(): SingleCategoryStatistic { return this.generateCategoryStatistic(this.emissionsPer100g); }
    get categoryStatisticCalories(): SingleCategoryStatistic { return this.generateCategoryStatistic(this.caloriesPer100g); }

    constructor(i?: IFood) {
        if (i !== undefined) {
            this.id = i.id;
            this.name = i.name;
            this.caloriesPerKg = i.caloriesPerKg;
            this.footprintPerKg = i.footprintPerKg;
            if (i.footprint) { this.footprint = new Footprint(i.footprint); }
            this.carbsPer100g = i.carbsPer100g;
            if (i.carbsKnown) { this.carbsKnown = i.carbsKnown; }
            this.fatPer100g = i.fatPer100g;
            if (i.fatKnown) { this.fatKnown = i.fatKnown; }
            this.proteinPer100g = i.proteinPer100g;
            if (i.proteinKnown) { this.proteinKnown = i.proteinKnown; }
            this.wastePercentage = i.wastePercentage;
            this.possibleUnits = [];
            if (i.possibleUnits) {
                i.possibleUnits.forEach(possibleUnit => {
                    this.possibleUnits.push(new Unit(possibleUnit));
                });
            }
            this.category = i.category;
            this.subCategory = i.subCategory;
            this.lastChangedAt = new Date(i.lastChangedAt);
        }
        if (!this.possibleUnits) { this.possibleUnits = []; }
    }

    toInterface(): IFood {
        const possibleUnits: IUnit[] = [];
        this.possibleUnits.forEach(element => {
            possibleUnits.push(element.toInterface());
        });
        // This check is necessary to make sure old data (before makro data was introduced) is not causing trouble
        // in the backend. For some reason, strange dates are submitted which are no valid json
        const lastChangedAt = this.lastChangedAt.getFullYear() > 2000 ? this.lastChangedAt : undefined;
        return {id: this.id, name: this.name, caloriesPerKg: this.caloriesPerKg, footprintPerKg: this.footprintPerKg,
            footprint: this.footprint?.toInterface(), carbsPer100g: this.carbsPer100g, carbsKnown: this.carbsKnown,
            fatPer100g: this.fatPer100g, fatKnown: this.fatKnown, proteinPer100g: this.proteinPer100g,
            proteinKnown: this.proteinKnown, wastePercentage: this.wastePercentage, possibleUnits, category: this.category,
            subCategory: this.subCategory, lastChangedAt};
    }

    private generateCategoryStatistic(value: number): SingleCategoryStatistic {
        if (!!this.subCategory) {
            return new SingleCategoryStatistic(this.category, value, this.subCategory);
        }
        return new SingleCategoryStatistic(this.category, value);
    }
}

export class MealFood extends Base {
    private get amountInGram(): number { return this.unit.toGram(this.amount); }
    private get amountIn100g(): number { return this.amountInGram / 100; }
    private get amountInKg(): number { return this.amountInGram / 1000; }
    get emissions(): number { return this.amountInKg * this.food.footprintPerKg * 1000; } // Emissions ins grams
    get calories(): number { return this.amountInKg * this.food.caloriesPerKg; }
    get carbsKnown(): boolean { return this.food.carbsKnown; }
    get carbs(): number { return this.carbsKnown ? (this.food.carbsPer100g * this.amountIn100g) : 0; }
    get fatKnown(): boolean { return this.food.fatKnown; }
    get fat(): number { return this.fatKnown ? (this.food.fatPer100g * this.amountIn100g) : 0; }
    get proteinKnown(): boolean { return this.food.proteinKnown; }
    get protein(): number { return this.proteinKnown ? (this.food.proteinPer100g * this.amountIn100g) : 0; }
    get macrosKnown(): boolean { return this.carbsKnown && this.proteinKnown && this.fatKnown; }
    get name(): string { return this.food.name; }
    get imgUrl(): string {return this.food.imgUrl; }

    get categoryStatisticEmissions(): SingleCategoryStatistic { return this.generateCategoryStatistic(this.emissions); }
    get categoryStatisticCalories(): SingleCategoryStatistic { return this.generateCategoryStatistic(this.calories); }

    constructor(food: Food | IMealFood | MealItem){
        super();
        if (food instanceof Food){
            this._food = food;
            this.amount = 0;
            // First unit in possibile units array is the default unit
            if (this.food.possibleUnits && this.food.possibleUnits.length > 0) { this.unit = this.food.possibleUnits[0]; }
        }else if (food instanceof MealItem){
            if (food.originalItem instanceof Food){
                this._food = new Food(food.originalItem.toInterface());
            }
            else{
                this._food = new Food();
            }
            this.amount = food.amount;
            this.unit = new Unit(food.unit);
        }else{
            this._food = new Food(food.food);
            this.amount = food.amount;
            this.unit = new Unit(food.unit);
        }
    }

    get amountWithUnit(): string {
        if (this.unit.name === 'g' || this.unit.name === 'ml') { return this.amount + this.unit.name; }
        if (this.unit.name === 'Packung' || this.unit.name === 'Portion') { return this.amount + ' ' + this.unit.name; }
        return this.amount + ', ' + this.unit.name;
    }
    amount: number;
    unit: Unit;
    private _food: Food;
    get food(): Food { return this._food; }

    static deepCopyArray(mealFoods: MealFood[]): MealFood[] {
        const ret: MealFood[] = [];
        mealFoods.forEach(element => {
            ret.push(element.deepCopy());
        });
        return ret;
    }

    toInterface(): IMealFood {
        return {amount: this.amount, unit: this.unit.toInterface(), food: this.food.toInterface()};
    }

    deepCopy(): MealFood {
        return new MealFood(this.toInterface());
    }

    equals(mealFood: MealFood): boolean{
        return this.amount === mealFood.amount && this.unit.equals(mealFood.unit) && this.food.id === mealFood.food.id;
    }

    equalsDeep(mealFood: MealFood): boolean {
        if (!this.equals(mealFood)) { return false; }
        return this.food.lastChangedAt.getTime() === mealFood.food.lastChangedAt.getTime();
    }

    public updateMacros(carbs: number, fat: number, protein: number): boolean {
        this._food.carbsPer100g = carbs;
        this._food.proteinPer100g = protein;
        this._food.fatPer100g = fat;
        this._food.carbsKnown = true;
        this._food.proteinKnown = true;
        this._food.fatKnown = true;
        return true;
    }

    private generateCategoryStatistic(value: number): SingleCategoryStatistic {
        const ret = this.food.categoryStatisticEmissions; // as the value will be overwritten, it doesn't matter if emissions or calories
        ret.value = value;
        return ret;
    }
}

export class Unit {
    name: string;
    conversionToGram: number;
    baseUnit = 'g';

    constructor(i?: IUnit) {
        if (i) {
            this.name = i.name;
            this.conversionToGram = i.conversionToGram;
            if (i.baseUnit) { this.baseUnit = i.baseUnit; }
        }
    }

    public toGram(value: number) {
        return value * this.conversionToGram;
    }

    public fromGram(value: number) {
        return value / this.conversionToGram;
    }

    public toString(): string {
        if (this.name === 'g' || this.name === 'ml') { return this.name; }
        return this.name + ' (' + Math.round(this.conversionToGram) + this.baseUnit + ')';
    }

    public toInterface(): IUnit {
        return {name: this.name, conversionToGram: this.conversionToGram, baseUnit: this.baseUnit};
    }

    public equals(unit: Unit): boolean {
        return this.name === unit.name && this.conversionToGram === unit.conversionToGram;
    }
}

export class RecentFood {
    food: Food;
    count: number;

    constructor(food: Food){
        this.food = food;
        this.count = 1;
    }
}

export class RecentFoods {
    private _foods: RecentFood[] = [];
    public get foods(): Food[] { return this.generateFoods(); }

    public reportFoodAppearance(food: Food) {
        const index = this._foods.findIndex((f) => f.food.id === food.id);
        if (index === -1) {
            this._foods.push(new RecentFood(food));
            return;
        }
        this._foods[index].count++;
        this.sort();
    }

    private sort() {
        this._foods = this._foods.sort((a, b) => a.count > b.count ? -1 : (a.count < b.count ? 1 : 0));
    }

    private generateFoods(): Food[]{
        const foods: Food[] = [];
        this._foods.forEach(f => {
            foods.push(f.food);
        });
        return foods;
    }
}

export interface IFood {
    id: string;
    name: string;
    caloriesPerKg: number;
    footprintPerKg: number;
    footprint: IFootprint;
    carbsPer100g: number;
    carbsKnown: boolean;
    fatPer100g: number;
    fatKnown: boolean;
    proteinPer100g: number;
    proteinKnown: boolean;
    wastePercentage: number;
    possibleUnits: IUnit[];
    category: string;
    subCategory: string;
    lastChangedAt: Date;
}

export function isIFood(object: any): object is IFood {
    return !('brand' in object);
}

export interface IMealFood {
    amount: number;
    unit: IUnit;
    food: IFood;
}

export function isIMealFood(object: any): object is IMealFood {
    return 'food' in object;
}

export interface IUnit {
    name: string;
    conversionToGram: number;
    baseUnit: string;
}
